<template>
  <modal :show.sync="visible" @update:show="handleModalClose" class="dark-modal">
    <div class="modal-box">
      <div class="modal-box-header">
        <h2 class="modal-title">{{ $t('users.filter.title') }}</h2>
      </div>

      <div class="user-filter-content">
        <!-- Roles Section -->
        <div class="field user-filter-section">
          <label class="label user-filter-section-label">{{ $t('users.filter.roles') }}</label>
          <div class="field is-grouped is-grouped-multiline user-filter-options">
            <div class="control" v-for="role in availableRoles" :key="role.value">
              <label class="checkbox">
                <input
                  type="checkbox"
                  :value="role.value"
                  v-model="localFilters.scopes"
                  @change="updateResultCount"
                >
                {{ role.label }}
              </label>
            </div>
          </div>
        </div>

        <!-- Status Section -->
        <div class="field user-filter-section">
          <label class="label user-filter-section-label">{{ $t('users.filter.status') }}</label>
          <div class="field is-grouped is-grouped-multiline user-filter-options">
            <div class="control" v-for="status in availableStatuses" :key="status.value">
              <label class="checkbox">
                <input
                  type="checkbox"
                  :value="status.value"
                  v-model="localFilters.statuses"
                  @change="updateResultCount"
                >
                {{ status.label }}
              </label>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button
          type="button"
          class="button cancel-button"
          @click="clearAllFilters"
        >
          {{ $t('users.filter.clearAll') }}
        </button>
        <button
          type="button"
          class="button is-primary"
          @click="confirmFilters"
          :disabled="isLoading"
          :class="{ 'is-loading': isLoading }"
        >
          {{ $t('users.filter.confirm') }}
          <span v-if="resultCount !== null" class="tag is-light ml-2">
            {{ resultCount }}
          </span>
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import { Modal } from '@cyradar/ui'
import { debounce } from '@/utils'

export default {
  name: 'UserFilter',
  components: {
    Modal
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Object,
      default: () => ({
        scopes: [],
        statuses: []
      })
    }
  },
  data() {
    return {
      visible: false,
      localFilters: {
        scopes: [],
        statuses: []
      },
      resultCount: null,
      isLoading: false
    }
  },
  computed: {
    /**
     * Available role options for filtering
     */
    availableRoles() {
      return [
        { value: 'root', label: this.$t('users.roles.root') },
        { value: 'admin', label: this.$t('users.roles.admin') },
        { value: 'user', label: this.$t('users.roles.user') },
        { value: 'monitor', label: this.$t('users.roles.monitor') }
      ]
    },
    /**
     * Available status options for filtering
     */
    availableStatuses() {
      return [
        { value: 'active', label: this.$t('users.filter.active') },
        { value: 'inactive', label: this.$t('users.filter.inactive') }
      ]
    }
  },
  watch: {
    show: {
      immediate: true,
      handler(newValue) {
        this.visible = newValue
        if (newValue) {
          this.initializeFilters()
        }
      }
    },
    filters: {
      deep: true,
      immediate: true,
      handler(newFilters) {
        if (newFilters) {
          this.localFilters = {
            scopes: [...(newFilters.scopes || [])],
            statuses: [...(newFilters.statuses || [])]
          }
        }
      }
    }
  },
  created() {
    // Create debounced function for result count updates
    this.debouncedUpdateResultCount = debounce(() => {
      this.fetchResultCount()
    }, 500)
  },
  methods: {
    /**
     * Initialize filters when modal opens
     */
    initializeFilters() {
      this.localFilters = {
        scopes: [...(this.filters.scopes || [])],
        statuses: [...(this.filters.statuses || [])]
      }
      this.updateResultCount()
    },
    /**
     * Handle modal close event
     */
    handleModalClose(value) {
      this.$emit('update:show', value)
    },
    /**
     * Update result count when filters change
     */
    updateResultCount() {
      if (this.visible) {
        this.debouncedUpdateResultCount()
      }
    },
    /**
     * Fetch result count from API based on current filters
     */
    async fetchResultCount() {
      if (!this.hasActiveFilters()) {
        this.resultCount = null
        return
      }

      this.isLoading = true
      try {
        const response = await this.$http.get('/api/v1/users/', {
          params: {
            scopes: this.localFilters.scopes.length > 0 ? this.localFilters.scopes.join(',') : undefined,
            statuses: this.localFilters.statuses.length > 0 ? this.localFilters.statuses.join(',') : undefined,
            count_only: true
          }
        })

        if (response && response.data && response.data.data) {
          this.resultCount = response.data.data.total || 0
        }
      } catch (error) {
        console.error('Error fetching result count:', error)
        this.resultCount = 0
      } finally {
        this.isLoading = false
      }
    },
    /**
     * Check if any filters are currently active
     */
    hasActiveFilters() {
      return this.localFilters.scopes.length > 0 || this.localFilters.statuses.length > 0
    },
    /**
     * Confirm and apply filters
     */
    confirmFilters() {
      this.$emit('apply-filters', {
        scopes: [...this.localFilters.scopes],
        statuses: [...this.localFilters.statuses],
        resultCount: this.resultCount // Pass the result count to parent
      })
      // Close modal after a slight delay to ensure event processing
      this.$nextTick(() => {
        this.handleModalClose(false)
      })
    },
    /**
     * Clear all filters
     */
    clearAllFilters() {
      this.localFilters = {
        scopes: [],
        statuses: []
      }
      this.resultCount = null
    },
    /**
     * Cancel filter changes
     */
    cancelFilters() {
      this.handleModalClose(false)
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/styles/dark-modal.scss';

.user-filter-content {
  min-height: 150px;
  padding: 1rem 0;
}

/* Section styling for better visual separation */
.user-filter-section {
  margin-bottom: 1.5rem;
}

.user-filter-section:last-child {
  margin-bottom: 0.75rem;
}

/* Section label styling with improved spacing */
.user-filter-section-label {
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 0.5rem !important;
  color: $modal-text;
}

/* Options container with proper spacing */
.user-filter-options {
  margin-top: 0.5rem;
}

/* Checkbox styling for better alignment and spacing */
.checkbox {
  margin-right: 1rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  color: $modal-text;
}

.checkbox input[type="checkbox"] {
  margin-right: 0.5rem;
}

/* Utility class for spacing */
.tag.ml-2 {
  margin-left: 0.5rem;
}
</style>
